import type { Person } from '@tive/core';
import { useEffect, useState } from 'react';
import type { Viewer } from '../lib/api';
import { resolveTheme } from '../lib/theme';
import { cn, departmentColor } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { Avatar } from './Avatar';

/**
 * Who else has this plan open, along the top.
 *
 * Two people editing one scenario is the point of sharing it, and without this
 * the first anybody knows about the other is somebody else's change arriving on
 * their screen. Faces are cheaper than an explanation.
 *
 * Nothing here is fetched or stored: presence arrives over the socket and
 * empties when it goes, because it is a claim about right now and a stale one is
 * worse than none.
 */

/** Faces before the row gives up and counts the rest. */
const SHOWN = 4;
const FACE = 24;
/** How far each face laps the one before it — `-space-x-1.5`. */
const OVERLAP = 6;

/**
 * The width the row always takes, however many people are in it.
 *
 * Fixed, and that is the whole point of the number. The row sits between the
 * scenario menu and the view tabs, so a face arriving used to shove every
 * control to its right — somebody about to click Reporting clicks Teams
 * instead, because a colleague opened the plan. Room for `SHOWN` faces and the
 * overflow count is the most it can ever need.
 */
const ROW_WIDTH = FACE * (SHOWN + 1) - OVERLAP * SHOWN;

const ROLE_LABELS = { owner: 'Owner', writer: 'Editor', reader: 'Viewer' } as const;

export function Viewers() {
  const viewers = useAppStore((s) => s.viewers);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  /*
   * Shown even when it is only you.
   *
   * It used to hide below two, on the grounds that a row of one face is noise —
   * but the row is the only thing that says presence is working at all, and its
   * absence reads as "nobody is here" and "this feature does not exist" in
   * exactly the same way. One face answers the question; none leaves it open.
   */
  if (viewers.length === 0) return null;

  const shown = viewers.slice(0, SHOWN);
  const extra = viewers.length - shown.length;
  const summary =
    viewers.length === 1
      ? 'You are the only one with this scenario open'
      : `${viewers.length} people have this scenario open`;

  return (
    <div className="relative hidden shrink-0 md:block">
      <button
        type="button"
        aria-label={summary}
        aria-expanded={open}
        title={summary}
        onClick={() => setOpen((was) => !was)}
        style={{ width: ROW_WIDTH }}
        className="flex h-9 items-center rounded-lg px-0.5 transition-colors hover:bg-[var(--color-surface-2)]"
      >
        <span className="flex items-center -space-x-1.5">
          {shown.map((viewer) => (
            <Face key={viewer.id} viewer={viewer} />
          ))}
          {extra > 0 ? (
            <span
              className="flex size-6 items-center justify-center rounded-full border-2 border-[var(--color-surface)] bg-[var(--color-surface-2)] text-[9px] font-bold text-[var(--color-ink-2)]"
              aria-hidden
            >
              +{extra}
            </span>
          ) : null}
        </span>
      </button>

      {open ? (
        <>
          <button
            type="button"
            aria-label="Close the viewer list"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute top-10 left-0 z-20 w-64 rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-1.5 shadow-[var(--shadow-panel)]">
            <p className="px-2 pt-1 pb-1.5 text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
              Looking at this plan
            </p>
            <ul>
              {viewers.map((viewer) => (
                <li key={viewer.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5">
                  <Face viewer={viewer} size={28} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] text-[var(--color-ink)]">
                      {viewer.name}
                    </span>
                    {viewer.email ? (
                      <span className="block truncate text-[10.5px] text-[var(--color-ink-3)]">
                        {viewer.email}
                      </span>
                    ) : null}
                  </span>
                  {/*
                   * Drive's answer or nothing. It is absent for Claude, and for
                   * anybody who has not listed their own scenarios yet — saying
                   * "Editor" on a guess is telling somebody they may change a
                   * plan that Drive will refuse their write to.
                   */}
                  {viewer.role ? (
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                        viewer.role === 'owner'
                          ? 'bg-[var(--color-brand-soft)] text-[var(--color-brand)]'
                          : 'bg-[var(--color-surface-2)] text-[var(--color-ink-3)]',
                      )}
                    >
                      {ROLE_LABELS[viewer.role]}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * One face.
 *
 * The photo is the one already on their card, looked up by email rather than
 * taken from their Google token: the app has faces for the company from the
 * directory and from `photos.json`, and one person should not wear two
 * different pictures in two corners of the same screen. Anybody the org does
 * not know — Claude, or somebody signed in who is not in the snapshot — falls
 * back to initials, which is what `Avatar` does with a person who has no photo.
 */
function Face({ viewer, size = FACE }: { viewer: Viewer; size?: number }) {
  const person = useAppStore((s) =>
    viewer.email
      ? s.model?.people.find((candidate) => candidate.email?.toLowerCase() === viewer.email?.toLowerCase())
      : undefined,
  );
  const dark = useAppStore((s) => resolveTheme(s.theme) === 'dark');

  // Hashed off the name, the same way a department gets its swatch, so one
  // person keeps one colour from session to session.
  const colors = departmentColor(viewer.name, dark);
  const stand: Person = person ?? {
    ...BLANK,
    id: viewer.id,
    name: viewer.isClaude ? 'AI' : viewer.name,
    email: viewer.email,
  };

  return (
    <Avatar person={stand} size={size} colors={colors} className="border-2 border-[var(--color-surface)]" />
  );
}

/** Enough of a person for an avatar, for somebody the org has never heard of. */
const BLANK: Person = {
  id: '',
  employeeNumber: null,
  name: '',
  firstName: null,
  lastName: null,
  email: null,
  title: '',
  department: null,
  teams: [],
  location: null,
  country: null,
  managerId: null,
  startDate: null,
  status: 'Active',
  isNew: false,
};
